'use-client';
import * as React from 'react';
import type { MouseEventHandler } from 'react';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import type {
  IStylableButtonCorvidStateProps,
  IStylableButtonProps,
  IStylableButtonImperativeActions,
} from '../../../StylableButton.types';
import stylableButtonSemanticClassNames from '../../../StylableButton.semanticClassNames';
import { buildCorvidStyleStates } from '../../utils';
import ButtonComponent from './Button';
import styles from './Button.module.scss';
import extendedStyles from './extendedStyleApi.module.scss';

function getCorvidStyleStatesClassNames(
  corvid: IStylableButtonCorvidStateProps | undefined,
) {
  // Excluding non-style-state props that would leak into the style states map
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    iconSvgString,
    iconCollapsed,
    iconAnimationTriggers,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    ...corvidStyleProps
  } = corvid ?? {};
  /* eslint-enable @typescript-eslint/no-unused-vars */
  const styleStates = buildCorvidStyleStates(corvidStyleProps);
  const stylesMap = extendedStyles as Record<string, string>;
  return Object.keys(styleStates)
    .filter(className => styleStates[className] && stylesMap[className])
    .map(className => stylesMap[className]);
}

const TRANSITION_DURATION = 400;

type ButtonSkinProps = IStylableButtonProps & {
  extendedStyleApi?: boolean;
  labelClass?: string;
  iconClass?: string;
  builderAnimationIcon?: React.ComponentType<any>;
};

const ButtonSkin: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  ButtonSkinProps
> = (props, ref) => {
  const {
    className,
    isDisabled,
    direction,
    corvid,
    extendedStyleApi = true,
    iconClass = '',
    labelClass = '',
    stylableButtonClassName,
    customClassNames = [],
    isMaxContent = false,
    isWrapText = false,
    fallbackDirectionClassName,
    onMouseEnter: propsOnMouseEnter,
    onMouseLeave: propsOnMouseLeave,
  } = props;

  const [active, setActive] = React.useState(false);
  const animationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const semanticClassNames =
    props.semanticClassNames || stylableButtonSemanticClassNames;

  const handleMouseEnter = React.useCallback<MouseEventHandler>(
    event => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }

      setActive(true);
      propsOnMouseEnter?.(event);
    },
    [propsOnMouseEnter],
  );

  const handleMouseLeave = React.useCallback<MouseEventHandler>(
    event => {
      // Set timeout to remove animations after 0.4s (matching CSS transition duration)
      animationTimeoutRef.current = setTimeout(() => {
        setActive(false);
      }, TRANSITION_DURATION);
      propsOnMouseLeave?.(event);
    },
    [propsOnMouseLeave],
  );

  React.useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const rootClassName = classNames(
    styles.root,
    className,
    stylableButtonClassName,
    !direction && (fallbackDirectionClassName ?? styles.rootDirection),
    formatClassNames(semanticClassNames.root, ...customClassNames),
    {
      disabled: isDisabled,
      isMaxContent,
      isWrapText,
      [extendedStyles.root]: extendedStyleApi,
      [styles.active]: active,
    },
    getCorvidStyleStatesClassNames(corvid),
  );

  const labelClassName = classNames(
    labelClass,
    extendedStyleApi && extendedStyles.label,
    styles.label,
    formatClassNames(semanticClassNames.buttonLabel),
  );

  const iconClassName = classNames(
    iconClass,
    extendedStyleApi && extendedStyles.icon,
    styles.icon,
    formatClassNames(semanticClassNames.buttonIcon),
  );

  const linkClassName = styles.link;

  return (
    <ButtonComponent
      {...props}
      ref={ref}
      rootClassName={rootClassName}
      labelClassName={labelClassName}
      iconClassName={iconClassName}
      linkClassName={linkClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );
};

export default React.forwardRef(ButtonSkin);
